import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { MarketMakingPlatform, MarketMakingPlatformControllerService } from '@api/core';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { environment } from 'environments/environment';
import { catchError, defer, finalize, map, Observable, of, shareReplay, timeout } from 'rxjs';
import {
  MarketMakingPlatform as MarketMakingPlatformVm,
  toPlatformId,
  toRoutePath,
} from './market-making-platform.model';
import { MarketMakingServicesFactory } from './market-making-services.factory';

const REQUEST_TIMEOUT_MS = 15_000;

export function toPlatformUrls(apiPath: string | undefined): { apiBasePath: string; wsBasePath: string } | undefined {
  const path = apiPath?.trim().replace(/^\/+/, '');
  if (!path) {
    return undefined;
  }
  if (/^(https?|wss?):\/\//.test(path)) {
    return { apiBasePath: path, wsBasePath: path.replace(/^http/, 'ws') };
  }
  const base = `${environment.envBaseUrl}/${path}`;
  return { apiBasePath: `https://${base}`, wsBasePath: `wss://${base}` };
}

export const MarketMakingPlatformsStore = signalStore(
  { providedIn: 'root' },
  withState({
    rows: new Array<MarketMakingPlatform>(),
    platformsLoaded: false,
    platformsLoadFailed: false,
  }),
  withProps(() => ({
    _platformsService: inject(MarketMakingPlatformControllerService),
    _notificationService: inject(NotificationService),
    _servicesFactory: inject(MarketMakingServicesFactory),
  })),
  withComputed(store => {
    const platforms = computed<MarketMakingPlatformVm[]>(() =>
      store
        .rows()
        .filter(row => !row.hidden && !!row.name && row.id != null)
        .flatMap(row => {
          const urls = toPlatformUrls(row.apiPath);
          if (!urls) {
            return [];
          }
          const displayName = row.displayName?.trim() || row.name!;
          const routePath = toRoutePath(displayName);
          const id = toPlatformId(routePath);
          return [
            {
              id,
              routePath,
              displayName,
              backendId: row.id!,
              backendName: row.name!,
              apiBasePath: urls.apiBasePath,
              wsBaseUrl: `${urls.wsBasePath}/v1/stream`,
              wsStatusKey: `${id.charAt(0).toUpperCase()}${id.slice(1)}WS`,
              hedgeAccountId: row.hedgeAccountId ?? undefined,
              services: store._servicesFactory.forBasePath(urls.apiBasePath),
            },
          ];
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    );

    return { platforms };
  }),
  withMethods(store => {
    const byId = (platformId: string): MarketMakingPlatformVm | undefined =>
      store.platforms().find(platform => platform.id === platformId);

    const isHedgeAccount = (account: { id?: number } | undefined): boolean =>
      !!account?.id && store.platforms().some(platform => platform.hedgeAccountId === account.id);

    const platformIdForHedgeAccount = (account: { id?: number } | undefined) =>
      account?.id == null ? undefined : store.platforms().find(platform => platform.hedgeAccountId === account.id)?.id;

    let inFlight: Observable<boolean> | undefined;

    const _fetch = (): Observable<boolean> =>
      defer(() => {
        patchState(store, { platformsLoaded: false, platformsLoadFailed: false });

        return store._platformsService.getMarketMakingPlatforms();
      }).pipe(
        timeout(REQUEST_TIMEOUT_MS),
        map((rows: MarketMakingPlatform[]) => {
          patchState(store, { rows, platformsLoaded: true, platformsLoadFailed: false });

          const dropped = rows.filter(row => !row.hidden && !toPlatformUrls(row.apiPath));
          if (dropped.length) {
            store._notificationService.showErrorNotification(
              `No API path configured for: ${dropped.map(row => row.name).join(', ')}. ` +
                'These platforms are unavailable until one is set in Settings.',
            );
          }

          return true;
        }),
        catchError((error: HttpErrorResponse) => {
          store._notificationService.showErrorNotification('Failed to load market making platforms', error);
          patchState(store, { platformsLoaded: true, platformsLoadFailed: true });

          return of(false);
        }),
        finalize(() => (inFlight = undefined)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    const platformsReady = (): Observable<boolean> =>
      store.platformsLoaded() && !store.platformsLoadFailed() ? of(true) : (inFlight ??= _fetch());

    const reloadPlatforms = () => {
      inFlight = _fetch();
      inFlight.subscribe();
    };

    return { byId, isHedgeAccount, platformIdForHedgeAccount, platformsReady, reloadPlatforms };
  }),
);
